/**
 * Deterministic synthetic player for fuzzing and cross-engine determinism
 * digests (brief §11.1): given a seed, produces a plausible stream of
 * InputFrames — held sweeps across the field (which link chains), wandering
 * between them, and choice answers whenever the sim opens a choice point.
 * Pure; fence-safe.
 */

import type { InputFrame } from '@hnplay/shared';
import { CHOICE_INDEX_SHIFT, INPUT_BIT_PRIMARY } from '@hnplay/shared';
import { createRng, type Rng } from './prng.js';

export interface ScriptedInput {
  next(choiceOpen: boolean): InputFrame;
}

export function createScriptedInput(seed: string): ScriptedInput {
  const rng: Rng = createRng(`input:${seed}`);
  let ax = 0;
  let ay = 0;
  // sweep = press-and-drag in a straight line (this is what links chains);
  // the press begins a few ticks INTO the sweep so tap-driven games (Stack)
  // see drops at varied crane positions rather than always at the edge.
  let sweepTicks = 0;
  let sweepVx = 0;
  let sweepVy = 0;
  let pressDelay = 0;
  let restTicks = 30 + rng.nextInt(90);
  let choiceDelay = -1;

  const clamp = (v: number): number => (v > 32767 ? 32767 : v < -32768 ? -32768 : v);

  return {
    next(choiceOpen: boolean): InputFrame {
      let bits = 0;

      if (choiceOpen) {
        // React to a choice with a human-ish delay, then answer 1..3.
        if (choiceDelay < 0) choiceDelay = 12 + rng.nextInt(60);
        if (choiceDelay === 0) {
          bits |= (1 + rng.nextInt(3)) << CHOICE_INDEX_SHIFT;
        }
        choiceDelay -= 1;
        return { bits, axisX: ax, axisY: ay };
      }
      choiceDelay = -1;

      if (sweepTicks > 0) {
        // Mid-sweep: drag along the line; hold PRIMARY once the delay elapses.
        if (pressDelay > 0) pressDelay -= 1;
        else bits |= INPUT_BIT_PRIMARY;
        ax = clamp(ax + sweepVx);
        ay = clamp(ay + sweepVy);
        sweepTicks -= 1;
      } else if (restTicks > 0) {
        // Between sweeps: released, small wander.
        ax = clamp(ax + rng.nextInt(1201) - 600);
        ay = clamp(ay + rng.nextInt(1201) - 600);
        restTicks -= 1;
      } else {
        // Start a new sweep from a fresh spot, aimed across the field.
        ax = clamp(rng.nextInt(65536) - 32768);
        ay = clamp(rng.nextInt(65536) - 32768);
        sweepVx = rng.nextInt(1301) - 650;
        sweepVy = rng.nextInt(1301) - 650;
        sweepTicks = 50 + rng.nextInt(140);
        restTicks = 10 + rng.nextInt(50);
        pressDelay = 4 + rng.nextInt(60);
      }

      return { bits, axisX: ax, axisY: ay };
    },
  };
}
